import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { ProposalAccessResponse } from '@shared/api';
import { ApiError, getProposalAccess } from '../api/client';
import { navigate } from '../state/router';
import { ProposalView } from './ProposalView';
import { TeammateView } from './TeammateView';

/**
 * /p/:id — asks the server who the signed-in user is to this proposal
 * (docs/viewer-sharing.md) before mounting anything that talks to it. The
 * owner gets the full workspace; a view-only teammate gets TeammateView,
 * which never mounts the agent bar or any write path.
 */
export function ProposalRoute({ proposalId }: { proposalId: string }): ReactElement {
  const [access, setAccess] = useState<ProposalAccessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void getProposalAccess(proposalId)
      .then((res) => {
        if (live) setAccess(res);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        // 404 = no such proposal, or one this account doesn't own (e.g. the
        // tab still shows another account's proposal after a sign-in). The
        // server won't say which; either way the rep's own list is the
        // useful place to land, not a dead-end error.
        if (cause instanceof ApiError && cause.status === 404) {
          navigate('/', { replace: true });
          return;
        }
        setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
  }, [proposalId]);

  if (error) return <p className="loading">{error}</p>;
  if (!access) return <p className="loading">Loading proposal…</p>;
  if (access.access === 'viewer') {
    return <TeammateView proposalId={proposalId} ownerUsername={access.ownerUsername} />;
  }
  return <ProposalView proposalId={proposalId} />;
}
